export default function Programs() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const programs = [
    {
      id: 1,
      name: 'Kids Ninja Classes',
      forWho: 'Ages 5-12',
      description: 'Fun, age-appropriate obstacle training that builds confidence, coordination, and strength while making fitness exciting for young ninjas.',
      icon: 'ri-user-smile-line',
      image: 'https://static.readdy.ai/image/d6b4b88b420755e3ba36fdd8ab8e8672/909eb7da635efc1eec7a3e0298ffe453.png',
      cta: 'Book Now'
    },
    {
      id: 2,
      name: 'Teen Ninja Training',
      forWho: 'Ages 13-17',
      description: 'Advanced obstacle courses and strength training designed for teens ready to take their ninja skills to the next level with competitive elements.',
      icon: 'ri-run-line',
      image: 'https://storage.readdy-site.link/project_files/8c606bec-a1c4-4337-82da-c52a0844d7ac/88b4a88f-37a5-4321-9fd1-99f9412d282d_IMG_20260415_000350.jpg?v=34b90c7a89e46b5690ab108df66bfc78',
      cta: 'View Schedule'
    },
    {
      id: 3,
      name: 'Adult Ninja Training',
      forWho: 'Ages 18+',
      description: 'High-intensity functional fitness combining obstacle training, strength work, and cardio for adults seeking a unique and challenging workout.',
      icon: 'ri-sword-line',
      image: 'https://public.readdy.ai/ai/img_res/17307c736f328c9b012a7df876dfb6e1.jpg',
      cta: 'Book Now'
    },
    {
      id: 4,
      name: 'Open Gym',
      forWho: 'All Ages & Levels',
      description: 'Self-directed training time to practice skills, work on specific obstacles, and train at your own pace in our fully equipped facility.',
      icon: 'ri-time-line',
      image: 'https://storage.readdy-site.link/project_files/8c606bec-a1c4-4337-82da-c52a0844d7ac/612f299a-b051-4964-8441-9a8f02c46a15_IMG_20260415_000405.jpg?v=885a3e978a0d314ddce3e9f0ab8b2946',
      cta: 'View Schedule'
    },
    {
      id: 5,
      name: 'Drop-In Sessions',
      forWho: 'All Levels Welcome',
      description: 'Try ninja training without commitment. Perfect for visitors, first-timers, or those with flexible schedules who want to experience our facility.',
      icon: 'ri-door-open-line',
      image: 'https://storage.readdy-site.link/project_files/8c606bec-a1c4-4337-82da-c52a0844d7ac/9abfc86a-9fe9-40cf-80f7-8ebbb8259e7d_485154164_1155708296565529_523311042957914287_n.jpg?v=5971d354bb47a82fef807fafda5edb42',
      cta: 'Book Now'
    }
  ];

  return (
    <section id="programs" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="mb-4">
            <span className="text-[#FF6B35] text-sm font-bold uppercase tracking-wider">
              Programs
            </span>
          </div>
          <h2 className="text-5xl md:text-6xl font-black text-[#0A1628] mb-6">
            Find Your Challenge
          </h2>
          <p className="text-gray-600 text-xl max-w-3xl mx-auto">
            Whether you're just starting out or training for competition, we have the perfect program to match your goals and skill level.
          </p>
        </div>

        {/* Programs Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {programs.slice(0, 3).map((program, index) => (
            <div
              key={program.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group hover:-translate-y-2"
              style={{ marginTop: index === 1 ? '2rem' : '0' }}
            >
              {/* Image Area */}
              <div className="relative h-60 overflow-hidden">
                <img
                  src={program.image}
                  alt={program.name}
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] to-transparent opacity-60"></div>
                
                {/* Icon */}
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 w-16 h-16 rounded-full bg-[#FF6B35] flex items-center justify-center">
                  <i className={`${program.icon} text-white text-3xl`}></i>
                </div>

                {/* Hover Accent Line */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-[#FF6B35] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              </div>

              {/* Content */}
              <div className="p-8">
                <h3 className="text-2xl font-bold text-[#0A1628] mb-2">
                  {program.name}
                </h3>
                <p className="text-[#FF6B35] text-sm font-semibold mb-4">
                  {program.forWho}
                </p>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {program.description}
                </p>
                <button 
                 onClick={() => scrollToSection('contact')}
                className="w-full bg-[#FF6B35] text-white py-3 rounded-full font-semibold hover:bg-[#ff5722] transition-all duration-300 flex items-center justify-center group whitespace-nowrap cursor-pointer">
                  {program.cta}
                  <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Row - 2 Cards Centered */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {programs.slice(3).map((program) => (
            <div
              key={program.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group hover:-translate-y-2"
            >
              {/* Image Area */}
              <div className="relative h-60 overflow-hidden">
                <img
                  src={program.image}
                  alt={program.name}
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] to-transparent opacity-60"></div>
                
                {/* Icon */}
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 w-16 h-16 rounded-full bg-[#FF6B35] flex items-center justify-center">
                  <i className={`${program.icon} text-white text-3xl`}></i>
                </div>

                {/* Hover Accent Line */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-[#FF6B35] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              </div>

              {/* Content */}
              <div className="p-8">
                <h3 className="text-2xl font-bold text-[#0A1628] mb-2">
                  {program.name}
                </h3>
                <p className="text-[#FF6B35] text-sm font-semibold mb-4">
                  {program.forWho}
                </p>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {program.description}
                </p>
                <button 
                 onClick={() => scrollToSection('contact')}
                className="w-full bg-[#FF6B35] text-white py-3 rounded-full font-semibold hover:bg-[#ff5722] transition-all duration-300 flex items-center justify-center group whitespace-nowrap cursor-pointer">
                  {program.cta}
                  <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}